import { useState, useEffect, useCallback } from 'react';
import {
  Conversation,
  ConversationMember,
  getConversations,
  getConversationDetail,
  getConversationDownloadUrl,
  getRecordingDownloadUrl,
  getGroupsByOrg,
  Group,
} from '../../services/admin';

const PAGE_SIZE = 50;

const formatDuration = (ms?: number | null): string => {
  if (!ms) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/** Conversations group related PTT recordings (same network + talk group, close together in
 * time) - see documentation/architecture.md "Conversation grouping". Automatic grouping is primary;
 * this page is read/play/download-focused, matching the project's decision to defer a full
 * manual-editing UI (see documentation/status.md). */
export default function ConversationsManager({ orgId }: { orgId: string }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [talkGroupId, setTalkGroupId] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [members, setMembers] = useState<ConversationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const groupName = useCallback(
    (id?: string | null) => groups.find((g) => g.id === id)?.name ?? (id ? id.substring(0, 8) : '—'),
    [groups]
  );

  const load = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const data = await getConversations(orgId, {
          talk_group_id: talkGroupId || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        setConversations(data.conversations);
        setTotal(data.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load conversations');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orgId, talkGroupId, offset]
  );

  useEffect(() => {
    if (!orgId) return;
    getGroupsByOrg(orgId).then(setGroups).catch(() => setGroups([]));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    load();
    const interval = setInterval(() => load(true), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, talkGroupId, offset]);

  useEffect(() => {
    setOffset(0);
  }, [talkGroupId]);

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    setMembersLoading(true);
    try {
      const detail = await getConversationDetail(orgId, id);
      setMembers(detail.recordings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation detail');
    } finally {
      setMembersLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">💬 Conversations</h2>
        <button onClick={() => load()} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          🔄 Refresh
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>}

      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 flex gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Talk group</label>
          <select value={talkGroupId} onChange={(e) => setTalkGroupId(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm min-w-[180px]">
            <option value="">All</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">No conversations found</div>
      ) : (
        <div className="space-y-2">
          {conversations.map((c) => (
            <div key={c.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <button onClick={() => toggleExpand(c.id)} className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-700/50">
                <div>
                  <p className="font-medium">
                    {groupName(c.talk_group_id)} · {new Date(c.started_at).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(c.started_at).toLocaleTimeString()} – {new Date(c.ended_at).toLocaleTimeString()} · {formatDuration(c.duration_ms)} · {c.recording_count} PTT{c.recording_count === 1 ? '' : 's'}
                  </p>
                </div>
                <span className="text-gray-400">{expandedId === c.id ? '▲' : '▼'}</span>
              </button>

              {expandedId === c.id && (
                <div className="border-t border-gray-700 p-4 space-y-3 bg-gray-900/40">
                  <div className="flex gap-2">
                    <audio controls preload="none" className="h-8 flex-1">
                      <source src={getConversationDownloadUrl(c.id)} />
                    </audio>
                    <a
                      href={getConversationDownloadUrl(c.id)}
                      className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded whitespace-nowrap"
                    >
                      ⬇ Download conversation
                    </a>
                  </div>

                  {membersLoading ? (
                    <p className="text-sm text-gray-400">Loading timeline...</p>
                  ) : (
                    <div className="space-y-1">
                      {members.map((m) => (
                        <div key={m.recording_id} className="flex items-center justify-between gap-3 text-sm py-1.5 px-2 rounded hover:bg-white/5">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-gray-500 w-6 text-right">{m.sequence}.</span>
                            <span className="text-gray-300 whitespace-nowrap">{new Date(m.started_at).toLocaleTimeString()}</span>
                            <span className="truncate">{m.radio_id ? m.radio_id.substring(0, 8) : 'unknown radio'}</span>
                            <span className="text-gray-500">{formatDuration(m.duration_ms)}</span>
                          </div>
                          {m.status === 'completed' ? (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <audio controls preload="none" className="h-7" style={{ maxWidth: 180 }}>
                                <source src={getRecordingDownloadUrl(m.recording_id)} />
                              </audio>
                              <a href={getRecordingDownloadUrl(m.recording_id)} className="text-xs bg-black/30 hover:bg-black/50 px-2 py-1 rounded">
                                ⬇
                              </a>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500 flex-shrink-0">{m.status}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-400 pt-2 border-t border-gray-700">
        <span>Showing {conversations.length === 0 ? 0 : offset + 1}-{offset + conversations.length} of {total}</span>
        <div className="flex items-center gap-2">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40">← Prev</button>
          <span>Page {currentPage} / {totalPages}</span>
          <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40">Next →</button>
        </div>
      </div>
    </div>
  );
}
